import { useMemo, useState } from 'react';
import type { FilmStock } from '../api/filmStocks';
import { compact, filterFilmStocks, stockDetail, stockLabel, UNKNOWN_FILM_STOCK } from '../utils/filmStocks';
import { Combobox } from './Combobox';

/** How many stocks the list shows before asking for more typing. */
const SHOWN = 50;

export interface FilmStockPickerProps {
  label: string;
  stocks: FilmStock[];
  onPick: (stock: FilmStock) => void;
  /**
   * Offer "Add ‘{text}’ as a new stock…" as the last option, unless a stock
   * already has exactly that name. Called with the typed text.
   */
  onAddNew?: (query: string) => void;
  hint?: string;
  /** Empty the box after a pick, as a search box does, rather than show the pick. */
  clearOnPick?: boolean;
  /** Offer "Unknown / unbranded" last, for film with no label to go on. */
  includeUnknown?: boolean;
  /** What the box shows to start with: the stock already chosen. */
  initialLabel?: string;
  /** Marks the field as failing validation, described by `errorId`. */
  invalid?: boolean;
  errorId?: string;
}

/** A searchable film-stock field: active stocks, grouped by brand. */
export function FilmStockPicker({
  label,
  stocks,
  onPick,
  onAddNew,
  hint,
  clearOnPick = false,
  includeUnknown = false,
  initialLabel = '',
  invalid,
  errorId,
}: FilmStockPickerProps) {
  const [query, setQuery] = useState(initialLabel);
  const matches = useMemo(() => filterFilmStocks(stocks, query, SHOWN), [stocks, query]);
  const unknown = includeUnknown && filterFilmStocks([UNKNOWN_FILM_STOCK], query).total > 0;
  const choices = unknown ? [...stocks, UNKNOWN_FILM_STOCK] : stocks;

  const typed = query.trim();
  const exact = [...stocks, UNKNOWN_FILM_STOCK].some(
    (s) => s.active && [s.name, stockLabel(s), ...s.aliases].some((n) => compact(n) === compact(typed)),
  );
  const action =
    onAddNew && typed && !exact ? { label: `Add ‘${typed}’ as a new stock…`, onChoose: () => onAddNew(typed) } : null;

  return (
    <Combobox
      label={label}
      hint={hint}
      query={query}
      onQueryChange={setQuery}
      groups={[
        ...matches.groups.map((g) => ({
          label: g.brand,
          options: g.stocks.map((s) => ({ id: s.stock_id, label: s.name, detail: stockDetail(s) })),
        })),
        ...(unknown
          ? [{ label: 'Not listed', options: [{ id: UNKNOWN_FILM_STOCK.stock_id, label: UNKNOWN_FILM_STOCK.name, detail: 'Expired, bulk-rolled or unlabelled film' }] }]
          : []),
      ]}
      total={matches.total + (unknown ? 1 : 0)}
      invalid={invalid}
      errorId={errorId}
      describeCount={(n) => `${n} ${n === 1 ? 'stock matches' : 'stocks match'}`}
      action={action}
      onSelect={(option) => {
        const stock = choices.find((s) => s.stock_id === option.id);
        if (!stock) return;
        setQuery(clearOnPick ? '' : stockLabel(stock));
        onPick(stock);
      }}
    />
  );
}
